import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Avatar } from "@/components/Avatar";
import { api, formatError, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Users, MessageSquare, Images, Flag, Ban, Check, Shield, Search, Wifi, TrendingUp, UserPlus, Clock } from "lucide-react";

export default function AdminDashboard() {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("users");
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    if (me && me.role !== "admin") nav("/lobby");
  }, [me, nav]);

  useEffect(() => {
    const loadAll = () => {
      api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {});
      api.get("/admin/activity").then(({ data }) => setActivity(data)).catch(() => {});
    };
    loadAll();
    // Refresh activity every 30s so "online now" stays live
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => nav("/lobby")}
          data-testid="admin-back-btn"
          className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Back to lobby
        </button>

        <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-fuchsia-400 font-semibold">
          <Shield className="w-3.5 h-3.5" /> Admin · god-mode
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
          the control room.
        </h1>

        {/* Overview — all-time totals */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="admin-overview">
          <Stat label="Users" value={overview?.users?.total} sub={`${overview?.users?.guests ?? "—"} guests · ${overview?.users?.banned ?? "—"} banned`} />
          <Stat label="Messages" value={overview?.messages?.total} />
          <Stat label="Rooms" value={overview?.rooms?.total} />
          <Stat label="Images" value={overview?.images?.total} />
          <Stat label="Open reports" value={overview?.reports?.open} warn />
        </div>

        {/* Live activity — now + rolling windows */}
        <div className="mt-4 rounded-3xl border border-fuchsia-500/20 bg-fuchsia-500/[0.03] p-5" data-testid="admin-activity">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wifi className="w-4 h-4 text-fuchsia-400" />
              <h2 className="font-display text-lg font-bold tracking-tight">Live activity</h2>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              auto-refresh · 30s
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <Stat
              label="Online now"
              value={activity?.online_now}
              icon={Wifi}
              accent="emerald"
              testid="stat-online-now"
            />
            <Stat label="Active · 24h" value={activity?.active?.d1} icon={TrendingUp} testid="stat-active-24h" />
            <Stat label="Active · 7d" value={activity?.active?.d7} icon={TrendingUp} testid="stat-active-7d" />
            <Stat label="Active · 30d" value={activity?.active?.d30} icon={TrendingUp} testid="stat-active-30d" />
            <Stat label="New · 24h" value={activity?.new_users?.d1} icon={UserPlus} accent="cyan" testid="stat-new-24h" />
            <Stat label="New · 7d" value={activity?.new_users?.d7} icon={UserPlus} accent="cyan" testid="stat-new-7d" />
            <Stat label="Msgs · 24h" value={activity?.messages?.d1} icon={MessageSquare} testid="stat-msgs-24h" />
          </div>
          {activity?.top_rooms_24h?.length > 0 && (
            <div className="mt-5" data-testid="admin-top-rooms">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Hottest rooms (last 24h)
              </div>
              <div className="flex flex-wrap gap-2">
                {activity.top_rooms_24h.map((r) => (
                  <div
                    key={r.room_id}
                    className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs"
                    data-testid={`top-room-${r.room_id}`}
                  >
                    <span className="font-semibold">{r.name}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{r.type}</span>
                    <span className="text-fuchsia-300">· {r.count} msgs</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-10 flex items-center gap-2 border-b border-white/5">
          <TabBtn id="users" icon={Users} label="Users" active={tab} setActive={setTab} />
          <TabBtn id="messages" icon={MessageSquare} label="Messages" active={tab} setActive={setTab} />
          <TabBtn id="images" icon={Images} label="Images" active={tab} setActive={setTab} />
        </div>

        <div className="mt-6">
          {tab === "users" && <UsersPanel />}
          {tab === "messages" && <MessagesPanel />}
          {tab === "images" && <ImagesPanel />}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, sub, warn, icon: Icon, accent, testid }) {
  const accentCls = warn
    ? "border-rose-500/30 bg-rose-500/5"
    : accent === "emerald"
      ? "border-emerald-500/30 bg-emerald-500/5"
      : accent === "cyan"
        ? "border-cyan-500/30 bg-cyan-500/5"
        : "border-white/5 bg-[#0c0c0f]";
  const iconCls = warn
    ? "text-rose-400"
    : accent === "emerald"
      ? "text-emerald-400"
      : accent === "cyan"
        ? "text-cyan-400"
        : "text-zinc-500";
  return (
    <div className={`rounded-2xl border ${accentCls} p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className={`w-3.5 h-3.5 ${iconCls}`} />}
      </div>
      <div className="font-display text-3xl font-black tracking-tight mt-1">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function TabBtn({ id, icon: Icon, label, active, setActive }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => setActive(id)}
      data-testid={`admin-tab-${id}`}
      className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border-b-2 transition ${
        isActive ? "border-fuchsia-500 text-white" : "border-transparent text-zinc-400 hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ---------------- Users panel ---------------- */
function UsersPanel() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: query || undefined, limit: 60 } });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => { e.preventDefault(); load(q); };

  const ban = async (u) => {
    const reason = window.prompt(`Reason for banning ${u.username}?`, "") || "";
    try {
      await api.post(`/admin/users/${u.id}/ban`, { reason });
      setItems((prev) => prev.map((x) => x.id === u.id ? { ...x, banned: true } : x));
      toast.success(`${u.username} banned`);
    } catch (err) { toast.error(formatError(err)); }
  };

  const unban = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unban`);
      setItems((prev) => prev.map((x) => x.id === u.id ? { ...x, banned: false } : x));
      toast.success(`${u.username} unbanned`);
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div>
      <form onSubmit={onSearch} className="flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by username or email…"
          className="text-input py-2 flex-1"
          data-testid="admin-users-search"
        />
        <button className="neon-btn py-2 px-5" data-testid="admin-users-search-submit">
          {loading ? "Loading…" : "Find"}
        </button>
      </form>
      <div className="text-xs text-zinc-500 mb-2">{total} users total</div>
      <div className="rounded-3xl border border-white/5 bg-[#0c0c0f] divide-y divide-white/5 overflow-hidden">
        {items.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-3 p-3 sm:p-4">
            <Avatar user={u} size={40} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate">{u.username}</span>
                {u.role === "admin" && <span className="text-[9px] uppercase tracking-widest text-fuchsia-300 border border-fuchsia-500/30 rounded-full px-1.5 py-0.5">admin</span>}
                {u.is_guest && <span className="text-[9px] uppercase tracking-widest text-amber-300 border border-amber-500/30 rounded-full px-1.5 py-0.5">guest</span>}
                {u.banned && <span className="text-[9px] uppercase tracking-widest text-rose-300 border border-rose-500/30 rounded-full px-1.5 py-0.5">banned</span>}
                {u.is_online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">{u.email || "—"} · joined {u.created_at?.slice(0, 10)}</div>
            </div>
            {u.role !== "admin" && (
              u.banned ? (
                <button onClick={() => unban(u)} data-testid={`admin-unban-${u.id}`} className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Unban</button>
              ) : (
                <button onClick={() => ban(u)} data-testid={`admin-ban-${u.id}`} className="text-xs px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Ban</button>
              )
            )}
          </div>
        ))}
        {items.length === 0 && !loading && <div className="p-6 text-center text-zinc-500 text-sm">No users match.</div>}
      </div>
    </div>
  );
}

/* ---------------- Messages panel ---------------- */
function MessagesPanel() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/messages", { params: { q: query || undefined, limit: 200 } });
      setItems(data);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search all messages across all rooms…"
          className="text-input py-2 flex-1"
          data-testid="admin-msg-search"
        />
        <button className="neon-btn py-2 px-5" data-testid="admin-msg-search-submit">
          {loading ? "Loading…" : "Find"}
        </button>
      </form>
      <div className="rounded-3xl border border-white/5 bg-[#0c0c0f] divide-y divide-white/5 overflow-hidden">
        {items.map((m) => (
          <div key={m.id} data-testid={`admin-msg-${m.id}`} className="p-4 text-sm">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              <span className="text-fuchsia-300">{m.room_name || m.room_id}</span>
              <span>· {m.username}</span>
              <span>· {new Date(m.created_at).toLocaleString()}</span>
              {m.kind === "image" && <span className="text-amber-300">· photo</span>}
              {m.masked && <span className="text-fuchsia-300">· masked</span>}
            </div>
            <div className="text-zinc-100 whitespace-pre-wrap break-words">
              {m.kind === "image" ? <em className="text-zinc-400">[image message · {m.image_path}]</em> : m.content}
            </div>
          </div>
        ))}
        {items.length === 0 && !loading && <div className="p-6 text-center text-zinc-500 text-sm">Nothing here.</div>}
      </div>
    </div>
  );
}

/* ---------------- Images panel ---------------- */
function ImagesPanel() {
  const [purpose, setPurpose] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (p = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/images", { params: { purpose: p || undefined, limit: 120 } });
      setItems(data.items);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterBtn label="All" active={!purpose} onClick={() => { setPurpose(""); load(""); }} />
        <FilterBtn label="Avatars" active={purpose === "avatar"} onClick={() => { setPurpose("avatar"); load("avatar"); }} />
        <FilterBtn label="Private" active={purpose === "private"} onClick={() => { setPurpose("private"); load("private"); }} />
        <FilterBtn label="Message images" active={purpose === "message-image"} onClick={() => { setPurpose("message-image"); load("message-image"); }} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {items.map((it) => <ImageTile key={it.id} it={it} />)}
      </div>
      {items.length === 0 && !loading && <div className="p-6 text-center text-zinc-500 text-sm">No images.</div>}
    </div>
  );
}

function FilterBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? "bg-fuchsia-500 border-fuchsia-500 text-white" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`}>
      {label}
    </button>
  );
}

function ImageTile({ it }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const token = getToken();
    let blobUrl = null;
    let cancelled = false;
    fetch(`${API}/files/${it.storage_path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error("fetch failed");
        const blob = await r.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setUrl(blobUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [it.storage_path]);
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <button
        onClick={() => url && setExpanded(true)}
        data-testid={`admin-img-${it.id}`}
        className="aspect-square rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0c] relative group"
      >
        {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse bg-white/5" />}
        <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-black/70 text-[10px] text-zinc-300 text-left truncate">
          {it.owner?.username || "—"} · {it.purpose}
        </div>
      </button>
      {expanded && url && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setExpanded(false)}>
          <img src={url} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </>
  );
}
